import React, { useState } from "react";
import {
  Download,
  Filter,
  Calendar,
  FileText,
  ChevronDown,
} from "lucide-react";

const PayslipDownload = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Sample employee data - this would come from your backend
  const employeeData = {
    employeeId: "EMP001",
    name: "John Doe",
    department: "Engineering",
    designation: "Senior Developer",
  };

  // Generate payslip data for the last year
  const generatePayslips = () => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const payslips = [];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    for (let i = 0; i < 12; i++) {
      let month = currentMonth - i;
      let year = currentYear;

      if (month < 0) {
        month += 12;
        year -= 1;
      }

      payslips.push({
        id: `PAY-${year}-${String(month + 1).padStart(2, "0")}`,
        month: months[month],
        year: year,
        monthNum: month + 1,
        basicSalary: 50000,
        hra: 15000,
        allowances: 10000,
        deductions: 5000,
        netSalary: 70000,
        status: "Generated",
        generatedDate: new Date(year, month, 28).toLocaleDateString("en-GB"),
      });
    }

    return payslips;
  };

  const [payslips] = useState(generatePayslips());

  // Filter payslips based on selected year and month
  const filteredPayslips = payslips.filter((slip) => {
    const yearMatch = slip.year === selectedYear;
    const monthMatch =
      selectedMonth === "all" || slip.monthNum === parseInt(selectedMonth);
    return yearMatch && monthMatch;
  });

  // Get unique years from payslips
  const availableYears = [...new Set(payslips.map((slip) => slip.year))].sort(
    (a, b) => b - a
  );

  const handleDownload = (payslip) => {
    // This would trigger actual PDF download from your backend
    console.log(`Downloading payslip for ${payslip.month} ${payslip.year}`);
    alert(
      `Downloading payslip for ${payslip.month} ${payslip.year}\nPayslip ID: ${payslip.id}`
    );
  };

  const months = [
    { value: "all", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Payslip Portal
              </h1>
              <p className="text-gray-600">Download your salary payslips</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">
                Employee ID:{" "}
                <span className="font-semibold">{employeeData.employeeId}</span>
              </p>
              <p className="text-lg font-semibold text-gray-800">
                {employeeData.name}
              </p>
              <p className="text-sm text-gray-600">
                {employeeData.designation} • {employeeData.department}
              </p>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-800">
                Filter Payslips
              </h2>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition"
            >
              {showFilters ? "Hide" : "Show"} Filters
              <ChevronDown
                className={`w-4 h-4 transform transition ${
                  showFilters ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${
              showFilters ? "block" : "hidden lg:grid"
            }`}
          >
            {/* Year Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Select Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Month Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Select Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              >
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold">{filteredPayslips.length}</span>{" "}
              payslip(s)
            </p>
            {(selectedMonth !== "all" ||
              selectedYear !== new Date().getFullYear()) && (
              <button
                onClick={() => {
                  setSelectedYear(new Date().getFullYear());
                  setSelectedMonth("all");
                }}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Payslips List */}
        <div className="grid grid-cols-1 gap-4">
          {filteredPayslips.length > 0 ? (
            filteredPayslips.map((payslip) => (
              <div
                key={payslip.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="bg-indigo-100 p-3 rounded-lg">
                      <FileText className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">
                        {payslip.month} {payslip.year}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        Payslip ID:{" "}
                        <span className="font-mono">{payslip.id}</span>
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Gross Salary:</span>
                          <span className="font-semibold text-gray-800 ml-2">
                            ₹
                            {(
                              payslip.basicSalary +
                              payslip.hra +
                              payslip.allowances
                            ).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Deductions:</span>
                          <span className="font-semibold text-red-600 ml-2">
                            -₹{payslip.deductions.toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Net Salary:</span>
                          <span className="font-semibold text-green-600 ml-2">
                            ₹{payslip.netSalary.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Generated on: {payslip.generatedDate}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                      {payslip.status}
                    </span>
                    <button
                      onClick={() => handleDownload(payslip)}
                      className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
                    >
                      <Download className="w-5 h-5" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                No Payslips Found
              </h3>
              <p className="text-gray-600">
                No payslips available for the selected filters.
              </p>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Payslips are generated on the last working
            day of each month. If you cannot find a payslip, please contact HR
            department.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PayslipDownload;
